/**
 * sliceActiveStatement — pure helper that carves the ACTIVE classified
 * statement's exact source text out of a (possibly multi-statement) buffer.
 *
 * Plan 6 §3.3 S8, decision 4: Execute always runs the active classified
 * statement only, never the whole buffer, so a multi-statement tab's
 * statement picker + Execute already cover multi-statement scripts. `null`
 * (no classified statement — e.g. an unparseable/whitespace-only buffer)
 * falls back to the whole buffer, trimmed.
 *
 * Byte-honest: only leading/trailing whitespace is trimmed off the sliced
 * range — no reformatting, no casing changes. Accepts any value shaped like
 * `Pick<StatementInfo, 'range'>` (structurally compatible with the real
 * `StatementInfo` from `sql-grammar/model`) so callers and tests don't need to
 * construct a fully-populated `StatementInfo`.
 */
import type { StatementInfo } from '../../../sql-grammar/model';

export type ActiveStatementRange = Pick<StatementInfo, 'range'>;

export function sliceActiveStatement(
  bufferText: string,
  statement: ActiveStatementRange | null,
): string {
  if (!statement) return bufferText.trim();
  const { startOffset, endOffset } = statement.range;
  return bufferText.slice(startOffset, endOffset).trim();
}
